import { createContext, ReactNode, useEffect, useState } from "react";
import axios from "../services/axios";
import { microAreaService } from "../services/microAreaService";
import { areaService } from "../services/areaService";
import { farmService } from "../services/farmService";

export type IOptionsEditOnboarding =
  | undefined
  | "farms"
  | "areas"
  | "micro-areas"
  | "batches"
  | "evaluations"
  | "profiles"
  | "weight-animals"
  | "animal-category"
  | "goals-birth"
  | "goals-death"
  | "create-products"
  | "task-evaluations"
  | "pasture-management"
  | "create-evaluator"
  | "batch-inventory";

type ICard = {
  id: number | null;
  area_id: number;
  initial: string;
  initialBatch?: string;
  name: string;
  qtdArea?: number;
  qtdMicroArea?: number;
  qtdAnimals?: number;
  managementType?: any;
  info?: any;
  description: string;
  state?: string;
  date?: string;
  style?: React.CSSProperties;
};

type EditOnboarding = {
  listCards: ICard[];
  defaultValue: any;
  filterSelected: IOptionsEditOnboarding;
  setFilterSelected: (value: IOptionsEditOnboarding) => void;
  optionForm: IOptionsEditOnboarding | undefined;
  setOptionForm: (value: IOptionsEditOnboarding | undefined) => void;
  setDefaultValue: (value: any) => void;
  idSelected: number;
  setIdSelected: (value: number | null) => void;
  farmIdSelected: number;
  setFarmIdSelected: (value: number) => void;
  areaIdSelected: number;
  setAreaIdSelected: (value: number) => void;
  cardInfo: ICard;
  setCardInfo: (value: ICard) => void;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  queryToSearch: string;
  setQueryToSearch: (query: string | undefined) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
};

type EditOnboardingProps = {
  children: ReactNode;
};

const EditOnboardingContext = createContext({} as EditOnboarding);

function EditOnboardingProvider({ children }: EditOnboardingProps) {
  const [filterSelected, setFilterSelected] = useState<IOptionsEditOnboarding>(undefined);
  const [listCards, setListCads] = useState<ICard[]>([]);
  const [cardInfo, setCardInfo] = useState<ICard>();
  const [optionForm, setOptionForm] = useState<IOptionsEditOnboarding>(undefined);
  const [farmIdSelected, setFarmIdSelected] = useState<number>(0);
  const [areaIdSelected, setAreaIdSelected] = useState<number>(0);
  const [idSelected, setIdSelected] = useState<number>(0);
  const [defaultValue, setDefaultValue] = useState<any>({});
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [queryToSearch, setQueryToSearch] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!optionForm) {
      setIdSelected(0);
      setDefaultValue(undefined);
    }
  }, [optionForm]);

  useEffect(() => {
    setQueryToSearch(undefined);
    setCurrentPage(1);
  }, [filterSelected]);

  useEffect(() => {
    if (filterSelected) {
      switch (filterSelected) {
        case "weight-animals":
        case "animal-category":
        case "goals-birth":
        case "goals-death":
        case "create-products":
        case "create-evaluator":
        case "task-evaluations":
          break;
        default:
          getCards();
          break;
      }
    }
  }, [queryToSearch, filterSelected, currentPage, optionForm, farmIdSelected]);

  useEffect(() => {
    setCurrentPage(1);
  }, [queryToSearch]);

  function getCards() {
    if (filterSelected && (filterSelected.includes("evaluation") || filterSelected.includes("task-evaluations")))
      return;

    setIsLoading(true);

    if (filterSelected == "batch-inventory") {
      if (farmIdSelected) {
        axios
          .get(`/${filterSelected}?farmId=${farmIdSelected}&page=${currentPage - 1}&size=8`)
          .then(({ data }) => {
            setListCads(data.content.map((card: any) => {
              return {
                id: card.task_id,
                initial: card.area_initials,
                name: card.name,
                state: card.state,
                date: card.updated_at ? card.updated_at : (card.created_at || undefined),
                area_id: card.area_id,
                description: `${card.batch_initials.length} Lotes`
              } as ICard;
            }));
            setTotalPages(Number(data.total_pages));
          })
          .finally(() => setIsLoading(false));
      }
    } else if (filterSelected != "pasture-management") {
      const toSearch = queryToSearch ? `&query=${queryToSearch}` : "";
      axios
        .get(`/${filterSelected}/search?page=${currentPage - 1}${toSearch}`)
        .then((response) => {
          const listCards: ICard[] = response.data.content.map((card: any) => {
            var fullInitials = "";
            var batchInitials = undefined;
            switch (filterSelected) {
              case "farms":
                fullInitials = card.initials;
                break;
              case "areas":
                fullInitials = `${card.farm_initials}-${card.initials}`;
                break;
              case "micro-areas":
                fullInitials = `${card.farm_initials}-${card.area_initials}-${card.initials}`;
                break;
              case "batches":
                fullInitials = `${card.farm_initials}-${card.area_string}`;
                batchInitials = `${card?.initials}`;
                break;
            }
            return {
              id: card.id,
              initial: fullInitials,
              initialBatch: batchInitials,
              name: card.name,
              qtdArea: card?.qtd_areas,
              qtdMicroArea: card?.qtd_micro_areas,
              qtdAnimals: card?.qtd_animals,
            } as ICard;
          });
          setListCads(listCards);
          setTotalPages(Number(response.data.total_pages));
        })
        .finally(() => setIsLoading(false));
    } else {
      axios
        .get(`/micro-area-management?page=${currentPage - 1}&size=8`)
        .then((response) => {
          const listCards: ICard[] = response.data.content.map((card: any) => {
            return {
              id: card.id,
              initial: card.micro_area[0].management.type?.name,
              initialBatch: card.micro_area[0].management.type.monitoring_types[0].name,
              managementType: card.micro_area[0].management,
              info: card
            } as ICard;
          });
          setListCads(listCards);
          setTotalPages(Number(response.data.total_pages));
        })
        .finally(() => setIsLoading(false));
    }
  }

  useEffect(() => {
    if (idSelected != 0) {
      if (filterSelected == "farms") {
        // Requisição da fazenda por idSelected
        farmService
          .getFarmByIdForForm(idSelected)
          .then((data) => setDefaultValue(data))
          .finally(() => setIsLoading(false));
      } else if (filterSelected == "areas") {
        // Requisição de retiro por idSelected
        areaService
          .getAreaByIdForForm(idSelected)
          .then((data) => setDefaultValue(data))
          .finally(() => setIsLoading(false));
      } else if (filterSelected == "micro-areas") {
        // Requisição de pasto por idSelected
        microAreaService
          .getMicroAreaByIdForForm(idSelected)
          .then((data) => setDefaultValue(data))
          .finally(() => setIsLoading(false));
      } else if (filterSelected == "batches") {
        // Requisição de lote por idSelected
        axios
          .get(`/batches/${idSelected}`)
          .then(({ data }) => {
            var defaultForm = {
              id: data?.id,
              farm_id: data?.farm_id,
              area_id: data?.area_id,
              initialsArea: `${data?.farm_initials}-${data?.area_initials}`,
              initials: data?.initials,
              current_micro_area_id: {
                value: `${data?.current_micro_area_id}`,
              },
              micro_area_route_ids: data?.micro_areas_in_route.map(
                (option: any) => ({
                  label: `${data?.farm_initials}-${data?.area_initials}-${option?.initials}`,
                  value: `${option?.id}`,
                })
              ),
              category: data?.batch_categories.map((option: any) => ({
                value: `${option?.id}`,
                info: option?.qtd,
              })),
            };

            setDefaultValue(defaultForm);
          })
          .finally(() => setIsLoading(false));
      }
    }
  }, [idSelected]);

  useEffect(() => {
    if (idSelected != 0 && !optionForm) setOptionForm(filterSelected);
  }, [defaultValue]);

  return (
    <EditOnboardingContext.Provider
      value={
        {
          filterSelected,
          setFilterSelected,
          listCards,
          optionForm,
          setOptionForm,
          idSelected,
          setIdSelected,
          cardInfo,
          setCardInfo,
          farmIdSelected,
          setFarmIdSelected,
          areaIdSelected,
          setAreaIdSelected,
          setDefaultValue,
          defaultValue,
          totalPages,
          currentPage,
          setCurrentPage,
          queryToSearch,
          setQueryToSearch,
          isLoading,
          setIsLoading
        } as EditOnboarding
      }
    >
      {children}
    </EditOnboardingContext.Provider>
  );
}

export { EditOnboardingContext, EditOnboardingProvider };
